/**
 * DocumentValidator - PRL Document Approval
 *
 * Split-view "Tinder-style" interface for rapid document validation.
 * Left: Queue of pending documents
 * Right: Document preview + approval actions
 *
 * Features:
 * - PDF/Image preview
 * - Quick approve/reject
 * - Expiry date input
 * - Keyboard shortcuts
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { AdminLayout } from './AdminLayout';
import {
  getPendingDocuments,
  verifyDocument,
  rejectDocument,
  PendingDocument,
} from '../../services/apiClient';
import { useAppStore } from '../../store/useAppStore';
import { QueueItem } from './document-validator/QueueItem';
import { DocumentPreview } from './document-validator/DocumentPreview';
import { ActionPanel } from './document-validator/ActionPanel';

export function DocumentValidator() {
  const [documents, setDocuments] = useState<PendingDocument[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useAppStore();
  const containerRef = useRef<HTMLDivElement>(null);

  // r2b11[1] + r2b11[2] (ronda 4): el borrador del ActionPanel (caducidad
  // corregida, notas, formulario de rechazo abierto) sube al padre por ref.
  // Antes el atajo Enter llamaba a handleApprove() SIN argumentos y aprobaba
  // con la caducidad que tecleó el propio trabajador — la fecha que RRHH
  // acababa de corregir se descartaba en silencio; y con el formulario de
  // rechazo abierto, Enter APROBABA lo que se estaba rechazando. Ref (no
  // estado): el handler global lee el valor en el instante del evento sin
  // repintar nada.
  const panelDraftRef = useRef({ expiresAt: '', notes: '', rejectOpen: false });
  const handlePanelDraft = useCallback(
    (draft: { expiresAt: string; notes: string; rejectOpen: boolean }) => {
      panelDraftRef.current = draft;
    },
    []
  );

  const selectedDocument = documents.find((d) => d.id === selectedId);

  // Sin `selectedId` en las dependencias: al estar en el useCallback, CADA
  // clic en un documento de la cola recreaba loadDocuments, el useEffect lo
  // redisparaba y la pantalla entera volvía al spinner de carga (ronda 3,
  // r2b11[8]). La selección se conserva con el updater funcional; los
  // botones de aprobar/rechazar ya actualizan la lista en local.
  const loadDocuments = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getPendingDocuments();
      setDocuments(data);
      setSelectedId((prev) => {
        if (data.length === 0) return null;
        if (prev != null && data.some((d) => d.id === prev)) return prev;
        return data[0].id;
      });
    } catch (error) {
      console.error('Failed to load documents:', error);
      showToast('Error al cargar documentos', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedDocument || isProcessing) return;
      if (e.key !== 'Enter' || e.shiftKey) return;

      // r2b11[2] (ronda 4): con el formulario de rechazo abierto, Enter NO
      // aprueba — nada. El gesto contrario al que se estaba haciendo no
      // puede validar documentación PRL. Quien quiera confirmar el rechazo
      // con Enter que deje el foco en «Confirmar rechazo»: el click nativo
      // del botón lo hace (el guard de botones de abajo no lo pisotea).
      if (panelDraftRef.current.rejectOpen) return;

      // Enter aprueba SOLO desde fuera de un campo. El atajo estaba en un
      // listener de `window`: escribir el motivo del rechazo y pulsar Enter
      // APROBABA el documento — el gesto contrario al que se estaba haciendo,
      // en la pantalla que valida la documentación de prevención de riesgos.
      const foco = document.activeElement as HTMLElement | null;
      const escribiendo =
        foco instanceof HTMLInputElement ||
        foco instanceof HTMLTextAreaElement ||
        foco instanceof HTMLSelectElement ||
        foco?.isContentEditable === true;

      // r2b11[2] (ronda 4): un BOTÓN con el foco también queda fuera del
      // atajo: el Enter nativo del botón decide (VALIDAR aprueba CON los
      // valores del panel — el mismo camino que el click —, los motivos
      // rápidos rellenan, «Confirmar rechazo» rechaza). El `preventDefault`
      // de aquí abajo se comía ese click nativo.
      const enBoton = foco instanceof HTMLButtonElement;

      if (escribiendo || enBoton) return;

      e.preventDefault();
      // r2b11[1] (ronda 4): CON lo tecleado en el panel — la caducidad
      // corregida y las notas —, exactamente lo mismo que envía el botón
      // VALIDAR. El atajo anunciado por la propia pantalla deja de tirar el
      // dato que RRHH acaba de escribir.
      const { expiresAt, notes } = panelDraftRef.current;
      handleApprove(expiresAt || undefined, notes || undefined);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedDocument, isProcessing]);

  const handleApprove = async (expiresAt?: string, notes?: string) => {
    if (!selectedDocument) return;

    setIsProcessing(true);
    try {
      await verifyDocument({
        document_id: selectedDocument.id,
        expires_at: expiresAt,
        notes,
      });
      showToast('Documento validado', 'success');

      // Remove from list and select next
      const currentIndex = documents.findIndex((d) => d.id === selectedDocument.id);
      const newDocs = documents.filter((d) => d.id !== selectedDocument.id);
      setDocuments(newDocs);

      if (newDocs.length > 0) {
        const nextIndex = Math.min(currentIndex, newDocs.length - 1);
        setSelectedId(newDocs[nextIndex].id);
      } else {
        setSelectedId(null);
      }
    } catch (error) {
      console.error('Failed to verify document:', error);
      showToast('Error al validar documento', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!selectedDocument) return;

    setIsProcessing(true);
    try {
      await rejectDocument({
        document_id: selectedDocument.id,
        reason,
      });
      showToast('Documento rechazado', 'success');

      // Remove from list and select next
      const currentIndex = documents.findIndex((d) => d.id === selectedDocument.id);
      const newDocs = documents.filter((d) => d.id !== selectedDocument.id);
      setDocuments(newDocs);

      if (newDocs.length > 0) {
        const nextIndex = Math.min(currentIndex, newDocs.length - 1);
        setSelectedId(newDocs[nextIndex].id);
      } else {
        setSelectedId(null);
      }
    } catch (error) {
      console.error('Failed to reject document:', error);
      showToast('Error al rechazar documento', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <AdminLayout
      title="Validador de Documentos"
      subtitle={`${documents.length} documentos pendientes`}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-brand border-t-transparent rounded-full" />
        </div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12">

          <h3 className="text-xl font-semibold text-ink mb-2">Todo validado!</h3>
          <p className="text-ink-2">No hay documentos pendientes de revision</p>
        </div>
      ) : (
        <div ref={containerRef} className="flex gap-6 h-[calc(100vh-180px)]">
          {/* Left: Document queue */}
          <div className="w-80 flex-shrink-0 bg-white rounded-lg border border-line overflow-hidden flex flex-col">
            <div className="p-4 border-b border-line bg-surface-2">
              <h2 className="font-semibold text-ink">Cola de documentos</h2>
              <p className="text-sm text-ink-2">{documents.length} pendientes</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {documents.map((doc) => (
                <QueueItem
                  key={doc.id}
                  document={doc}
                  isSelected={doc.id === selectedId}
                  onClick={() => setSelectedId(doc.id)}
                />
              ))}
            </div>
          </div>

          {/* Right: Preview and actions */}
          {selectedDocument ? (
            <div className="flex-1 flex flex-col gap-4">
              {/* Preview */}
              <div className="flex-1">
                <DocumentPreview document={selectedDocument} />
              </div>

              {/* Actions */}
              <ActionPanel
                document={selectedDocument}
                onApprove={handleApprove}
                onReject={handleReject}
                isProcessing={isProcessing}
                onDraftChange={handlePanelDraft}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-surface-2 rounded-lg">
              <p className="text-ink-2">Selecciona un documento de la cola</p>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
